import React, { useState } from 'react';

import { useEditor } from '@craftjs/core';
import { useLayer } from '@craftjs/layers';
import { ChevronDownIcon, Circle, GripVertical } from 'lucide-react';
import { cn, AccordionNavigationContent, AccordionNavigationItem, AccordionNavigationTrigger, Button, ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger, useAccordionContext } from 'twyne-new.ui'

import { useActions } from '@/components/builder/blocks/base';
import { usePalette } from '@/modules/cms/views/post/components/builder/context'

import useProp from '../../../hooks/useProp';
import EditableNodeName from '../../EditableNodeName';

export function Item({ data: { block: label, key: value }, children, onExpand }) {
  const { [label]: { palette } } = usePalette()
  const { connectors: { layer, layerHeader, drag }, depth, expanded, children: layerChildren } = useLayer(({ expanded }) => ({ expanded }))
  const isEditing = useProp(false)
  const [groupHovered, setGroupHovered] = useState(false)

  return <Context isEditing={isEditing} value={value} >
    <AccordionNavigationItem value={value} ref={dom => layer(dom)}>
      <AccordionNavigationTrigger
        ref={dom => {
          layerHeader(dom)
          drag(dom)
        }}
        className='group relative'
        value={value}
        hover
        onMouseEnter={() => setGroupHovered(true)}
        onMouseLeave={() => setGroupHovered(false)}
        onClick={e => e.preventDefault()}>
        <div className='flex min-w-64 items-center'>
          <GripVertical size={13} className="cursor-move opacity-0 group-hover:opacity-50 " />
          <div className="flex flex-grow items-center gap-2 w-full" style={{ paddingLeft: `${(depth + 1) * 8}px` }}>
            {palette?.icon || <Circle size='16' />}
            <EditableNodeName id={value} isEditing={isEditing} groupHovered={groupHovered} />
          </div>
          <ExpandButton isEditing={isEditing?.value} expanded={expanded} onExpand={onExpand} layerChildren={layerChildren} value={value} />
        </div>
      </AccordionNavigationTrigger>
      {children && <AccordionNavigationContent value={value}>
        {children}
      </AccordionNavigationContent>}
    </AccordionNavigationItem>
  </Context>
}

const ExpandButton = ({ isEditing, expanded, onExpand, layerChildren, value }) => {
  const { active } = useAccordionContext()
  const isActive = active === value
  const { ancestors } = useEditor(state => {
    const ancestors = []
    let nodeId = state.nodes[value]?.data?.parent
    while (nodeId) {
      ancestors.push(nodeId)
      nodeId = state.nodes[nodeId]?.data?.parent
    }
    return { ancestors }
  })
  const isDescendantOfActive = ancestors.includes(active)

  if (!layerChildren || !layerChildren.length || isEditing) {
    return null
  }

  return <>
    <Button variant='ghost' size='sm' className='-ml-2 opacity-0 py-0' onClick={() => onExpand()}>
      <ChevronDownIcon size={15} />
    </Button>
    <Button
      variant='ghost'
      size='sm'
      className={cn(
        'h-full absolute right-0 top-0',
        isActive && 'text-white hover:bg-blue-500! dark:hover:bg-blue-600! hover:text-white!',
        isDescendantOfActive && 'hover:bg-blue-400/20!')}
      onClick={() => onExpand()}
    >
      <ChevronDownIcon size={15} className={`opacity-50 group-hover:opacity-100 ${expanded ? 'rotate-180' : ''} shrink-0 transition-transform duration-200`} />
    </Button>
  </>
}

const Context = ({ children, isEditing, value }) => {
  const { duplicate, remove, visibility } = useActions(value)

  const handleRename = () => {
    isEditing.setValue(!isEditing.value)
  }

  return <ContextMenu>
    <ContextMenuTrigger>
      <div className={cn(visibility.hidden ? "opacity-40" : "")}>
        {children}
      </div>
    </ContextMenuTrigger>
    <ContextMenuContent>
      <ContextMenuItem onClick={handleRename}>
        Rename
      </ContextMenuItem>
      <ContextMenuItem onClick={remove.handler} disabled={!remove.enabled}>
        Delete
      </ContextMenuItem>
      <ContextMenuItem onClick={duplicate.handler} disabled={!duplicate.enabled}>
        Duplicate
      </ContextMenuItem>
      <ContextMenuItem onClick={visibility.handler} disabled={!visibility.enabled}>
        {visibility.hidden ? 'Show' : 'Hide'}
      </ContextMenuItem>
    </ContextMenuContent>
  </ContextMenu>
}
